import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTopbar } from "@/components/chrome/use-topbar";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { apiUsageQueryOptions } from "@/lib/api-usage";
import {
  API_USAGE_RANGES,
  CALL_TYPES,
  labelForType,
  type ApiUsageRange,
} from "@/lib/api-usage-schemas";
import { VolumeChart } from "@/components/api-usage/VolumeChart";
import { TypeTrendChart } from "@/components/api-usage/TypeTrendChart";
import { ProviderDonut } from "@/components/api-usage/ProviderDonut";
import { formatDate, formatTime } from "@/lib/format";

export const Route = createFileRoute("/_app/settings/api-usage")({
  beforeLoad: requireAdminRoute,
  component: ApiUsagePage,
});

const RANGE_LABEL: Record<ApiUsageRange, string> = {
  "7d": "7D",
  "30d": "30D",
  "3m": "3M",
  "6m": "6M",
};

const colorForType = (key: string): string =>
  CALL_TYPES.find((t) => t.key === key)?.color ?? "#beac7f";

// Last 6 calendar months as { value: "YYYY-MM", label: "May 2026" }.
function recentMonths(): { value: string; label: string }[] {
  const now = new Date();
  const out: { value: string; label: string }[] = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const value = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
    out.push({ value, label });
  }
  return out;
}

function ApiUsagePage() {
  const [range, setRange] = useState<ApiUsageRange>("30d");
  const months = useMemo(recentMonths, []);
  const [month, setMonth] = useState<string>(months[0]?.value ?? "");
  const { data, isLoading } = useQuery(apiUsageQueryOptions(range, month));

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "API usage" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  const summary = data?.monthSummary;
  const byTypeEntries = summary ? Object.entries(summary.byType).sort((a, b) => b[1] - a[1]) : [];
  const maxType = byTypeEntries[0]?.[1] ?? 0;

  const handleExportCsv = () => {
    if (!data) return;
    const header =
      "date,trigger,lenderLoans,borrowerLoans,property,transactions,negativeRemarks,borrowerContacts,total,status";
    const lines = data.runs.map((r) => {
      const v = (k: string) => r.byType[k] ?? 0;
      return [
        new Date(r.startedAt).toISOString(),
        r.trigger,
        v("plr.lenderLoans"),
        v("plr.borrowerLoans"),
        v("sfr.property"),
        v("sfr.transactions"),
        v("plr.negativeRemarks"),
        v("plr.borrowerContacts"),
        r.total,
        r.status,
      ].join(",");
    });
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `api-usage-${month}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 border-b border-rule pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">API usage</h1>
          <p className="text-sm text-muted">
            Radar (SFR &amp; PLR) API calls by calendar month. Counters reset on the 1st.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="rounded-md border border-rule-strong bg-surface px-3 py-2 text-xs font-bold text-ink focus:outline-none focus:ring-1 focus:ring-gold"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={!data || data.runs.length === 0}
            className="rounded-md border border-rule-strong bg-surface px-3.5 py-2 text-xs font-semibold text-ink disabled:opacity-60"
          >
            Export CSV
          </button>
        </div>
      </div>

      {isLoading || !summary ? (
        <p className="text-sm text-muted">Loading usage…</p>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Calls this month" value={summary.total.toLocaleString()}>
              {summary.prevMonthTotal > 0 ? (
                <DeltaFoot total={summary.total} prev={summary.prevMonthTotal} />
              ) : (
                <span>no prior-month data</span>
              )}
            </Kpi>
            <Kpi label="Runs this month" value={String(summary.runCount)}>
              avg <strong>{summary.avgPerRun.toFixed(1)}</strong> calls / run
            </Kpi>
            <Kpi
              label="Top call type"
              value={summary.topType ? labelForType(summary.topType.key) : "—"}
              small
            >
              {summary.topType ? `${summary.topType.count.toLocaleString()} calls` : "no calls yet"}
            </Kpi>
            <Kpi
              label="Provider split"
              value={`PLR ${summary.byProvider.plr} · SFR ${summary.byProvider.sfr}`}
              small
            >
              calls this month
            </Kpi>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.5fr)_340px]">
            {/* LEFT */}
            <div className="space-y-6">
              <Panel
                title="Call volume"
                right={
                  <div className="inline-flex overflow-hidden rounded-md border border-rule-strong">
                    {API_USAGE_RANGES.map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setRange(r)}
                        className={`border-l border-rule px-3 py-1.5 text-[11px] font-bold first:border-l-0 ${
                          range === r ? "bg-ink text-white" : "bg-surface text-muted"
                        }`}
                      >
                        {RANGE_LABEL[r]}
                      </button>
                    ))}
                  </div>
                }
              >
                <VolumeChart buckets={data.buckets} />
                <Legend />
              </Panel>

              <Panel title="Calls by type over time">
                <TypeTrendChart buckets={data.buckets} />
                <Legend />
              </Panel>

              <Panel title={`Runs in ${months.find((m) => m.value === month)?.label ?? month}`}>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[12.5px]">
                    <thead>
                      <tr className="border-b border-rule text-left text-[10px] uppercase tracking-wider text-muted">
                        <th className="px-3 py-2.5">Date</th>
                        <th className="px-3 py-2.5">Trigger</th>
                        <th className="px-3 py-2.5 text-right">Lender</th>
                        <th className="px-3 py-2.5 text-right">Borrower</th>
                        <th className="px-3 py-2.5 text-right">Property</th>
                        <th className="px-3 py-2.5 text-right">Total</th>
                        <th className="px-3 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.runs.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-6 text-center text-muted">
                            No runs this month.
                          </td>
                        </tr>
                      ) : (
                        data.runs.map((r) => (
                          <tr key={r.runId} className="border-b border-rule last:border-b-0">
                            <td className="px-3 py-3">
                              {formatDate(r.startedAt)}, {formatTime(r.startedAt)}
                            </td>
                            <td className="px-3 py-3 capitalize">{r.trigger}</td>
                            <td className="px-3 py-3 text-right tabular-nums">
                              {r.byType["plr.lenderLoans"] ?? 0}
                            </td>
                            <td className="px-3 py-3 text-right tabular-nums">
                              {r.byType["plr.borrowerLoans"] ?? 0}
                            </td>
                            <td className="px-3 py-3 text-right tabular-nums">
                              {r.byType["sfr.property"] ?? 0}
                            </td>
                            <td className="px-3 py-3 text-right font-bold tabular-nums">
                              {r.total}
                            </td>
                            <td className="px-3 py-3 uppercase">{r.status}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Panel>
            </div>

            {/* RIGHT rail */}
            <div className="space-y-6">
              <Card title="By call type">
                {byTypeEntries.length === 0 ? (
                  <p className="text-[12px] text-muted">No calls recorded this month yet.</p>
                ) : (
                  byTypeEntries.map(([key, count]) => (
                    <div key={key} className="mb-3 last:mb-0">
                      <div className="mb-1.5 flex justify-between text-[12px]">
                        <span className="font-semibold">{labelForType(key)}</span>
                        <span className="font-bold">{count.toLocaleString()}</span>
                      </div>
                      <div className="h-2.5 overflow-hidden rounded-full bg-paper-2">
                        <span
                          className="block h-full rounded-full"
                          style={{
                            width: `${maxType > 0 ? (count / maxType) * 100 : 0}%`,
                            background: colorForType(key),
                          }}
                        />
                      </div>
                    </div>
                  ))
                )}
              </Card>

              <Card title="Provider split">
                <ProviderDonut plr={summary.byProvider.plr} sfr={summary.byProvider.sfr} />
              </Card>

              <Card title="Month to date">
                <RailStat label="Calls so far" value={summary.total.toLocaleString()} />
                <RailStat label="Daily average" value={summary.dailyAverage.toFixed(1)} />
                <RailStat
                  label="Projected month-end"
                  value={`~${summary.projectedMonthEnd.toLocaleString()}`}
                />
                <RailStat label="Prev month" value={summary.prevMonthTotal.toLocaleString()} />
              </Card>
            </div>
          </div>

          <p className="border-l-2 border-tan-dark bg-paper-2 px-5 py-3.5 text-xs leading-relaxed text-muted">
            <strong className="text-ink">Per-type breakdown begins with this release.</strong>{" "}
            Monthly totals reconstruct from every recorded run; the by-type split and trend lines
            populate from runs going forward. Month boundaries are calendar months in Mountain Time.
          </p>
        </>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  small,
  children,
}: {
  label: string;
  value: string;
  small?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-rule bg-surface p-[18px]">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted">{label}</div>
      <div className={`mt-2 font-bold tracking-tight ${small ? "pt-1 text-xl" : "text-3xl"}`}>
        {value}
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted">{children}</div>
    </div>
  );
}

function DeltaFoot({ total, prev }: { total: number; prev: number }) {
  const pct = Math.round(((total - prev) / prev) * 100);
  const up = pct >= 0;
  return (
    <span>
      <span className={`font-bold ${up ? "text-neg" : "text-pos"}`}>
        {up ? "▲" : "▼"} {Math.abs(pct)}%
      </span>{" "}
      vs prev month
    </span>
  );
}

function Panel({
  title,
  right,
  children,
}: {
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-rule bg-surface">
      <header className="flex items-center justify-between border-b border-rule px-5 py-4">
        <h2 className="text-[13px] font-semibold uppercase tracking-wider text-ink">{title}</h2>
        {right}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-rule bg-surface p-5">
      <h3 className="mb-3.5 text-[13px] font-semibold uppercase tracking-wider text-ink">
        {title}
      </h3>
      {children}
    </div>
  );
}

function RailStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-3 flex justify-between border-b border-rule pb-3 text-[12px] last:mb-0 last:border-b-0 last:pb-0">
      <span className="font-semibold text-muted">{label}</span>
      <span className="font-bold text-ink">{value}</span>
    </div>
  );
}

function Legend() {
  return (
    <div className="mt-3.5 flex flex-wrap gap-x-4 gap-y-2">
      {CALL_TYPES.map((t) => (
        <span
          key={t.key}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-muted"
        >
          <i
            className="inline-block h-[11px] w-[11px] rounded-sm"
            style={{ background: t.color }}
          />
          {t.label}
        </span>
      ))}
    </div>
  );
}
